import { useState } from 'react';

import ArchiveRoundedIcon from '@mui/icons-material/ArchiveRounded';
import PublishRoundedIcon from '@mui/icons-material/PublishRounded';
import RestoreRoundedIcon from '@mui/icons-material/RestoreRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import {
  Alert,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import {
  useArchiveProduct,
  useProductPublication,
  usePublishProduct,
  useRestoreProduct,
  useSalesChannels,
  useUnpublishProduct,
} from '../../hooks';
import { formatDateTime } from '../../lib/format';
import { useCatalogPermissions } from '../../lib/useCatalogPermissions';
import type { ToastSeverity } from '../../lib/useToast';
import {
  COMMERCIAL_AVAILABILITY_LABELS,
  PRODUCT_CARD_STATUS_LABELS,
  LIFECYCLE_STATUS_LABELS,
  commercialAvailabilityColor,
  productCardStatusColor,
  PUBLICATION_STATUS_LABELS,
  publicationStatusColor,
} from '../../model/options';

interface ProductPublicationCardProps {
  productId: string;
  onResult: (severity: ToastSeverity, message: string) => void;
}

/**
 * Publication panel for the product edit page (user-story-catalog-007): shows the product's lifecycle and
 * per-channel publication state, and lets a merchant with `catalog.manage` publish / unpublish / archive it.
 * The storefront only shows PUBLISHED, non-ARCHIVED products, so this is the "author → go live" control.
 */
export function ProductPublicationCard({ productId, onResult }: ProductPublicationCardProps) {
  const { canManage } = useCatalogPermissions();
  const publicationQuery = useProductPublication(productId);
  const channelsQuery = useSalesChannels({ enabled: canManage });
  const publish = usePublishProduct();
  const unpublish = useUnpublishProduct();
  const archive = useArchiveProduct();
  const restore = useRestoreProduct();

  const [channelCode, setChannelCode] = useState('');
  const [confirmArchive, setConfirmArchive] = useState(false);

  const publication = publicationQuery.data;
  const channels = publication?.channels ?? [];
  const pending = publish.isPending || unpublish.isPending || archive.isPending || restore.isPending;
  const isPublishedAnywhere = channels.some((c) => c.publicationStatus === 'PUBLISHED');
  const isArchived = publication?.lifecycleStatus === 'ARCHIVED';
  const primaryAction = isArchived ? 'restore' : isPublishedAnywhere ? 'unpublish' : 'publish';

  const runPublish = () =>
    publish.mutate(
      { productId, channelCode: channelCode || undefined },
      {
        onSuccess: () => onResult('success', 'Товар опубликован.'),
        onError: (err) => onResult('error', err.message),
      },
    );

  const runUnpublish = () =>
    unpublish.mutate(
      { productId, channelCode: channelCode || undefined },
      {
        onSuccess: () => onResult('success', 'Товар снят с публикации.'),
        onError: (err) => onResult('error', err.message),
      },
    );

  const runArchive = () =>
    archive.mutate(
      { productId },
      {
        onSuccess: () => {
          setConfirmArchive(false);
          onResult('success', 'Товар архивирован.');
        },
        onError: (err) => {
          setConfirmArchive(false);
          onResult('error', err.message);
        },
      },
    );

  const runRestore = () =>
    restore.mutate(
      { productId },
      {
        onSuccess: () => onResult('success', 'Товар возвращён на модерацию.'),
        onError: (err) => onResult('error', err.message),
      },
    );

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          Публикация
        </Typography>
        <Divider sx={{ mb: 2 }} />

        {publicationQuery.isLoading ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              Загрузка статуса публикации…
            </Typography>
          </Stack>
        ) : publicationQuery.isError ? (
          <Alert severity="error">Не удалось загрузить статус публикации.</Alert>
        ) : (
          <Stack spacing={2}>
            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" alignItems="center">
              <Chip
                size="small"
                variant="outlined"
                color={productCardStatusColor(publication?.cardStatus ?? publication?.lifecycleStatus)}
                label={
                  PRODUCT_CARD_STATUS_LABELS[publication?.cardStatus ?? ''] ??
                  LIFECYCLE_STATUS_LABELS[publication?.lifecycleStatus ?? ''] ??
                  publication?.lifecycleStatus ??
                  'Активен'
                }
              />
              {publication?.commercialAvailability ? (
                <Chip
                  size="small"
                  variant="outlined"
                  color={commercialAvailabilityColor(publication.commercialAvailability)}
                  label={
                    COMMERCIAL_AVAILABILITY_LABELS[publication.commercialAvailability] ??
                    publication.commercialAvailability
                  }
                />
              ) : null}
              <Chip
                size="small"
                color={isPublishedAnywhere ? 'success' : 'default'}
                variant={isPublishedAnywhere ? 'filled' : 'outlined'}
                label={isPublishedAnywhere ? 'Опубликован' : 'Черновик'}
              />
            </Stack>

            {channels.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Не опубликован ни в одном канале продаж.
              </Typography>
            ) : (
              <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                {channels.map((c) => (
                  <Tooltip
                    key={c.channelCode}
                    title={c.publishedAt ? `Опубликован: ${formatDateTime(c.publishedAt)}` : ''}
                  >
                    <Chip
                      size="small"
                      variant="outlined"
                      color={publicationStatusColor(c.publicationStatus)}
                      label={`${c.channelCode}: ${
                        PUBLICATION_STATUS_LABELS[c.publicationStatus] ?? c.publicationStatus
                      }`}
                    />
                  </Tooltip>
                ))}
              </Stack>
            )}

            {canManage ? (
              <>
                <Divider />
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1.5}
                  alignItems={{ sm: 'center' }}
                  useFlexGap
                  flexWrap="wrap"
                >
                  {!isArchived ? (
                    <TextField
                      select
                      size="small"
                      label="Канал"
                      value={channelCode}
                      onChange={(e) => setChannelCode(e.target.value)}
                      disabled={pending}
                      sx={{ minWidth: 220 }}
                    >
                      <MenuItem value="">Канал по умолчанию</MenuItem>
                      {(channelsQuery.data ?? []).map((ch) => (
                        <MenuItem key={ch.code} value={ch.code}>
                          {ch.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  ) : null}
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    {primaryAction === 'publish' ? (
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={
                          publish.isPending ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            <PublishRoundedIcon />
                          )
                        }
                        disabled={pending}
                        onClick={runPublish}
                      >
                        Опубликовать
                      </Button>
                    ) : primaryAction === 'unpublish' ? (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={
                          unpublish.isPending ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            <VisibilityOffRoundedIcon />
                          )
                        }
                        disabled={pending}
                        onClick={runUnpublish}
                      >
                        Снять с публикации
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={
                          restore.isPending ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            <RestoreRoundedIcon />
                          )
                        }
                        disabled={pending}
                        onClick={runRestore}
                      >
                        Вернуть на модерацию
                      </Button>
                    )}
                    {!isArchived ? (
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        startIcon={
                          archive.isPending ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            <ArchiveRoundedIcon />
                          )
                        }
                        disabled={pending}
                        onClick={() => setConfirmArchive(true)}
                      >
                        Архивировать
                      </Button>
                    ) : null}
                  </Stack>
                </Stack>
              </>
            ) : (
              <Alert severity="info">У вас нет прав на публикацию товаров.</Alert>
            )}
          </Stack>
        )}
      </CardContent>

      <Dialog
        open={confirmArchive}
        onClose={() => {
          if (!archive.isPending) setConfirmArchive(false);
        }}
      >
        <DialogTitle>Архивировать товар?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Товар будет снят с публикации во всех каналах и удалён из витрины. Карточка сохранится в
            каталоге и может быть восстановлена позже.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmArchive(false)} disabled={archive.isPending}>
            Отмена
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={runArchive}
            disabled={archive.isPending}
            startIcon={
              archive.isPending ? <CircularProgress size={16} color="inherit" /> : <ArchiveRoundedIcon />
            }
          >
            Архивировать
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
